import {useAddNotification} from '@yandex-delivery/blocks'
import {type ServerError} from '@yandex-delivery/core/utils/network'
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useFormContext,
  useFormState,
  useFormValue,
} from '@yandex-delivery/form'
import {type Optional} from '@yandex-delivery/types'
import {cloneDeep, defaultTo, get, getErrorMessage, pipe} from '@yandex-delivery/utils'
import {localizePrice} from '@yandex-delivery/utils/formatter'
import type React from 'react'
import {useCallback} from 'react'

import {i18n} from 'src/shared/config'
import {OrderEventGoal, useFormUserEventHandler} from 'src/shared/hooks'
import type {Order} from 'src/shared/types'

import {useScrollToErrorField} from '../hooks/use-scroll-to-error-field'
import {fetchCreateNddOrder} from '../service/create-order'

export function useNddSubmitHandler(
  formRef: React.MutableRefObject<Optional<HTMLDivElement>>,
  onSuccess: (link?: string) => void,
) {
  const scrollToFirstError = useScrollToErrorField(formRef)
  const addNotification = useAddNotification()
  const handleReachGoal = useFormUserEventHandler()

  const {handleSubmit, control} = useFormContext<Order>()
  const {isSubmitting} = useFormState<Order>({control})

  /**
   * Срабатывает только когда форма валидна
   */
  const validFormHandler = useCallback<SubmitHandler<Order>>(
    async formValues => {
      const clonedValues = cloneDeep(formValues)
      const orderId = get('nddOrder.id', clonedValues)

      if (orderId === undefined) {
        return
      }

      try {
        handleReachGoal(OrderEventGoal.SubmitButtonClick)

        const sharingLink = await fetchCreateNddOrder(orderId, formValues.isBusiness)
        onSuccess(sharingLink)
        handleReachGoal(OrderEventGoal.SuccessOrderCreate)
      } catch (error) {
        // Задача на маппинг ошибок и переводов здесь - https://st.yandex-team.ru/CARGODEV-29958
        const message = pipe(
          error as ServerError,
          getErrorMessage,
          defaultTo(i18n('nddOrder.offer-confirm.base-error', 'Ошибка подтверждения заказа')),
        )

        addNotification(message, {type: 'error'})
      }
    },
    [addNotification, onSuccess, handleReachGoal],
  )
  /**
   * Срабатывает только когда форма невалидна
   */
  const invalidFormHandler = useCallback<SubmitErrorHandler<Order>>(() => {
    requestAnimationFrame(scrollToFirstError)
  }, [scrollToFirstError])

  return useCallback(() => {
    if (isSubmitting) return

    void handleSubmit(validFormHandler, invalidFormHandler)()
  }, [handleSubmit, isSubmitting, validFormHandler, invalidFormHandler])
}

export function useButtonTitle() {
  const {control, watch} = useFormContext<Order>()
  const {isSubmitting} = useFormState<Order>({control})
  const priceTitle = usePriceTitle()

  const orderId = watch('nddOrder')?.id
  const isOrderPending = watch('nddOrder')?.status === 'pending'

  const defaultText = i18n('cargo.create.form.order', 'Заказать')

  if (isSubmitting || isOrderPending) {
    return i18n('cargo.create.form.evaluate', 'Оцениваем...')
  }

  if (orderId === undefined) {
    return i18n('cargo.create.form.order', 'Заказать')
  }

  return priceTitle ? `${defaultText}・${priceTitle}` : defaultText
}

export function usePriceTitle() {
  const price = useFormValue<Order, 'nddOrder.price'>('nddOrder.price')
  const priceTotal = useFormValue<Order, 'nddOrder.totalPrice'>('nddOrder.totalPrice')

  // Price может быть 0, 0.00 и т.д. из-за чего показывается неверный результат, отсекаем эту возможность
  if (price === undefined || !parseInt(price)) {
    return undefined
  }

  const orderPrice = priceTotal || price

  const [priceValue = '', priceCurrency] = orderPrice.split(' ')

  return localizePrice(priceValue, priceCurrency)
}

/**
 * Причина блокировки кнопки "Заказать"
 */
export function useDisableReason() {
  const {control, watch} = useFormContext<Order>()
  const {isValid, isSubmitting} = useFormState<Order>({control})
  const isBusiness = watch('isBusiness')
  const isOrderPending = watch('nddOrder')?.status === 'pending'

  if (isValid) {
    return isBusiness
      ? i18n('form.new-tariff-line.submit-button.sub-title', 'После этого откроется личный кабинет для бизнеса')
      : undefined
  }

  return isOrderPending || isSubmitting
    ? undefined
    : i18n('cargo.create.fill-requirement-fields', 'Заполните обязательные поля')
}

/**
 * Причина блокировки кнопки "Заказать" в мобильной версии
 * На десктоп пока нет макета
 */
export function useDisableReasonMobile() {
  const {control, watch} = useFormContext<Order>()
  const {isValid, isSubmitting} = useFormState<Order>({control})
  const isOrderPending = watch('nddOrder')?.status === 'pending'

  if (isOrderPending || isSubmitting || isValid) {
    return undefined
  }

  return i18n('order.form.submit-button.unfilled', 'Заполните данные')
}
